import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { HelperFunctions } from '@shared/utils/HelperFunctions';
import {
  ImpersonateUserConfigField,
  ImpersonateUserOption,
  ImpersonateUserConfigRecord,
  ImpersonateUserConfigResponse,
  ImpersonateUserConfigUser,
} from '@app/core/models/impersonate-user-config.model';
import { HttpClientService } from '@core/interceptors';
import { LoggerService } from '@core/services/logger.service';
import { APP_CONSTANTS } from '@constants/app-constants';
import { environment } from '@env/environment';

const MAIN_CONFIG_KEYS = [
  'u_logo',
  'u_enable_imp',
  'u_enable_ai_cockpit',
  'u_ai_users',
  'u_imp_si_name',
  'u_imp_si_email',
  'u_imp_si_id',
  'u_imp_sp_name',
  'u_imp_sp_email',
  'u_imp_sp_id',
  'u_imp_cl_name',
  'u_imp_cl_email',
  'u_imp_cl_id',
] as const;

const MAIN_CONFIG_KEY_SET = new Set<string>(MAIN_CONFIG_KEYS);

@Injectable({ providedIn: 'root' })
export class ImpersonateUserConfigService {
  private readonly http = inject(HttpClientService);
  private readonly logger = inject(LoggerService);
  private readonly logoImagePathFallback: string = APP_CONSTANTS.general.LOGO_IMAGE_PATH;

  private readonly logoImagePathState = signal<string>(this.logoImagePathFallback);
  private readonly impersonateUsersState = signal<readonly ImpersonateUserOption[]>([]);
  private readonly isImpersonationEnabledState = signal(false);
  private readonly isAiCockpitEnabledState = signal(false);
  private readonly aiUsersState = signal<readonly string[]>([]);
  private readonly isLoadingState = signal(false);
  private isLoaded = false;
  private inFlightLoad: Promise<void> | null = null;

  readonly logoImagePath = this.logoImagePathState.asReadonly();
  readonly impersonateUsers = this.impersonateUsersState.asReadonly();
  readonly isImpersonationEnabled = this.isImpersonationEnabledState.asReadonly();
  readonly isAiCockpitEnabled = this.isAiCockpitEnabledState.asReadonly();
  readonly aiUsers = this.aiUsersState.asReadonly();
  readonly isLoading = this.isLoadingState.asReadonly();

  async loadMainConfig(forceRefresh = false): Promise<void> {
    if (this.inFlightLoad) {
      return this.inFlightLoad;
    }

    if (this.isLoaded && !forceRefresh) {
      return;
    }

    this.inFlightLoad = this.loadMainConfigInternal(forceRefresh).finally(() => {
      this.inFlightLoad = null;
    });

    return this.inFlightLoad;
  }

  private async loadMainConfigInternal(forceRefresh: boolean): Promise<void> {
    this.isLoadingState.set(true);

    try {
      const response = await firstValueFrom(
        this.http.get<ImpersonateUserConfigResponse>(APP_CONSTANTS.general.MAIN_CONFIG_ENDPOINT),
      );
      const record = this.extractMainConfigRecord(response);

      if (!record) {
        this.logoImagePathState.set(this.logoImagePathFallback);
        this.impersonateUsersState.set([]);
        this.isImpersonationEnabledState.set(false);
        this.isAiCockpitEnabledState.set(false);
        this.aiUsersState.set([]);
        this.isLoaded = false;
        return;
      }

      const normalizedRecord = this.normalizeConfigRecord(record);

      this.logoImagePathState.set(this.resolveLogoImagePath(normalizedRecord.u_logo));
      this.impersonateUsersState.set(this.mapImpersonateUsers(normalizedRecord));
      this.isImpersonationEnabledState.set(
        HelperFunctions.toBoolean(normalizedRecord.u_enable_imp),
      );
      this.isAiCockpitEnabledState.set(
        HelperFunctions.toBoolean(normalizedRecord.u_enable_ai_cockpit),
      );
      this.aiUsersState.set(HelperFunctions.csvToList(normalizedRecord.u_ai_users));
      this.isLoaded = true;
    } catch (error) {
      if (forceRefresh) {
        this.logger.error(
          'Failed to refresh main config from main-config endpoint',
          this.errorToString(error),
        );
      } else {
        this.logger.error(
          'Failed to load main config from main-config endpoint',
          this.errorToString(error),
        );
      }
      this.logoImagePathState.set(this.logoImagePathFallback);
      this.impersonateUsersState.set([]);
      this.isImpersonationEnabledState.set(false);
      this.isAiCockpitEnabledState.set(false);
      this.aiUsersState.set([]);
      this.isLoaded = false;
    } finally {
      this.isLoadingState.set(false);
    }
  }

  isAiCockpitVisibleForUser(effectiveUserSysId: string): boolean {
    if (!this.isAiCockpitEnabled()) {
      return false;
    }

    const normalizedSysId = effectiveUserSysId.trim();
    if (!normalizedSysId) {
      return false;
    }

    const allowedUsers = this.aiUsers();
    return allowedUsers.includes(normalizedSysId);
  }

  private resolveLogoImagePath(rawLogoPath: string | undefined): string {
    const logoPath = HelperFunctions.toCleanString(rawLogoPath);
    if (!logoPath) {
      return this.logoImagePathFallback;
    }

    if (/^https?:\/\//i.test(logoPath)) {
      return logoPath;
    }

    const serviceNowBase = environment.endpoints.serviceNow.replace(/\/+$/, '');
    const normalizedPath = logoPath.startsWith('/') ? logoPath : `/${logoPath}`;
    return `${serviceNowBase}${normalizedPath}`;
  }

  private mapImpersonateUsers(
    record: ImpersonateUserConfigRecord,
  ): readonly ImpersonateUserOption[] {
    const users: ImpersonateUserOption[] = [];

    this.appendImpersonateUsers(users, record.u_imp_si_user, {
      name: record.u_imp_si_name,
      email: record.u_imp_si_email,
      sys_id: record.u_imp_si_id,
    });
    this.appendImpersonateUsers(users, record.u_imp_sp_user, {
      name: record.u_imp_sp_name,
      email: record.u_imp_sp_email,
      sys_id: record.u_imp_sp_id,
    });
    this.appendImpersonateUsers(users, record.u_imp_client_user, {
      name: record.u_imp_cl_name,
      email: record.u_imp_cl_email,
      sys_id: record.u_imp_cl_id,
    });

    return users;
  }

  private appendImpersonateUsers(
    users: ImpersonateUserOption[],
    configuredUsers: readonly ImpersonateUserConfigUser[] | undefined,
    legacyUser: ImpersonateUserConfigUser,
  ): void {
    const sourceUsers = Array.isArray(configuredUsers) ? configuredUsers : [legacyUser];

    for (const user of sourceUsers) {
      const mappedUser = this.buildImpersonateUser(user.name, user.email, user.sys_id);
      if (mappedUser) {
        users.push(mappedUser);
      }
    }
  }

  private buildImpersonateUser(
    rawName: string | undefined,
    rawEmail: string | undefined,
    rawSysId: string | undefined,
  ): ImpersonateUserOption | null {
    const email = HelperFunctions.toCleanString(rawEmail);
    const providedName = HelperFunctions.toCleanString(rawName);
    const fallbackName = email ? (email.split('@')[0] ?? '') : '';
    const name = providedName || fallbackName;
    const sysId = HelperFunctions.toCleanString(rawSysId) || email;

    if (!name || !email) {
      return null;
    }

    return {
      name,
      email,
      sysId,
    };
  }

  private errorToString(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'Unknown error';
  }

  private extractMainConfigRecord(
    response: ImpersonateUserConfigResponse | undefined,
  ): ImpersonateUserConfigRecord | undefined {
    const payloadRecord = response?.result?.record;
    const fields = payloadRecord?.fields;

    if (!payloadRecord) {
      return undefined;
    }

    if (!Array.isArray(fields)) {
      return this.hasMainConfigKeyValues(payloadRecord) ? payloadRecord : undefined;
    }

    if (!fields.length) {
      return undefined;
    }

    const reconstructedRecord: Partial<ImpersonateUserConfigRecord> = {
      sys_id: HelperFunctions.toCleanString(payloadRecord.sys_id) || undefined,
    };

    for (const field of fields) {
      const keyCandidate = this.resolveEntryKey(field);
      if (!keyCandidate) {
        continue;
      }

      const entryValue = this.resolveEntryValue(field, keyCandidate);
      if (entryValue !== undefined) {
        (reconstructedRecord as Record<string, unknown>)[keyCandidate] = entryValue;
      }
    }

    return this.hasMainConfigKeyValues(reconstructedRecord)
      ? (reconstructedRecord as ImpersonateUserConfigRecord)
      : undefined;
  }

  private resolveEntryKey(field: ImpersonateUserConfigField): string | undefined {
    const rawKey = HelperFunctions.toCleanString(field.name);
    if (!rawKey || !MAIN_CONFIG_KEY_SET.has(rawKey)) {
      return undefined;
    }

    return rawKey;
  }

  private resolveEntryValue(
    field: ImpersonateUserConfigField,
    keyCandidate: string,
  ): string | undefined {
    if (keyCandidate === 'u_ai_users') {
      return HelperFunctions.toCleanString(field.sysID || field.display) || undefined;
    }

    return HelperFunctions.toCleanString(field.display || field.sysID) || undefined;
  }

  private hasMainConfigKeyValues(record: Partial<ImpersonateUserConfigRecord>): boolean {
    return (
      MAIN_CONFIG_KEYS.some((key) => record[key] !== undefined) ||
      record.u_client !== undefined ||
      record.u_imp_client_user !== undefined ||
      record.u_imp_sp_user !== undefined ||
      record.u_imp_si_user !== undefined
    );
  }

  private normalizeConfigRecord(record: ImpersonateUserConfigRecord): ImpersonateUserConfigRecord {
    return {
      sys_id: this.toCleanStringValue(record.sys_id),
      u_client: record.u_client,
      u_logo: this.toCleanStringValue(record.u_logo),
      u_enable_imp: this.toPrimitiveValue(record.u_enable_imp),
      u_enable_ai_cockpit: this.toPrimitiveValue(record.u_enable_ai_cockpit),
      u_ai_users: this.toCsvOrStringValue(record.u_ai_users),
      u_imp_si_email: this.toCleanStringValue(record.u_imp_si_email),
      u_imp_si_id: this.toCleanStringValue(record.u_imp_si_id),
      u_imp_si_name: this.toCleanStringValue(record.u_imp_si_name),
      u_imp_sp_email: this.toCleanStringValue(record.u_imp_sp_email),
      u_imp_sp_id: this.toCleanStringValue(record.u_imp_sp_id),
      u_imp_sp_name: this.toCleanStringValue(record.u_imp_sp_name),
      u_imp_cl_email: this.toCleanStringValue(record.u_imp_cl_email),
      u_imp_cl_id: this.toCleanStringValue(record.u_imp_cl_id),
      u_imp_cl_name: this.toCleanStringValue(record.u_imp_cl_name),
      u_imp_client_user: this.normalizeUsers(record.u_imp_client_user),
      u_imp_sp_user: this.normalizeUsers(record.u_imp_sp_user),
      u_imp_si_user: this.normalizeUsers(record.u_imp_si_user),
    };
  }

  private normalizeUsers(
    users: readonly ImpersonateUserConfigUser[] | undefined,
  ): readonly ImpersonateUserConfigUser[] | undefined {
    if (!Array.isArray(users)) {
      return undefined;
    }

    return users.map((user) => ({
      sys_id: this.toCleanStringValue(user?.sys_id),
      name: this.toCleanStringValue(user?.name),
      email: this.toCleanStringValue(user?.email),
    }));
  }

  private toPrimitiveValue(value: unknown): string | number | boolean | undefined {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }

    return undefined;
  }

  private toCleanStringValue(value: unknown): string | undefined {
    const primitive = this.toPrimitiveValue(value);
    if (primitive === undefined) {
      return undefined;
    }

    const normalized = HelperFunctions.toCleanString(String(primitive));
    return normalized || undefined;
  }

  private toCsvOrStringValue(value: unknown): string | undefined {
    return this.toCleanStringValue(value);
  }
}
